// Admin-API-Client. Spricht den Bertsy-Backend-Server an.
// Auth: Session-Token, geliefert durch /api/admin/auth/login.

// Default = Produktions-API. Override für lokale Entwicklung via VITE_API_BASE_URL.
const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'https://api.bertsy.de';

const TOKEN_KEY = 'bertsy_admin_session_token';

export function getToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) sessionStorage.setItem(TOKEN_KEY, token);
  else sessionStorage.removeItem(TOKEN_KEY);
}

async function call<T>(path: string, init?: RequestInit, requireAuth = true): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((init?.headers as Record<string, string>) ?? {}),
  };
  if (requireAuth) {
    if (!token) throw new Error('not_authenticated');
    headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  let json: any;
  try {
    json = await res.json();
  } catch {
    throw new Error(`HTTP ${res.status}`);
  }
  if (!res.ok || !json.ok) {
    // Bei Auth-Fehler: Token verwerfen, damit die UI zum Login zurückspringt
    if (res.status === 401) setToken(null);
    throw new Error(json.error ?? `HTTP ${res.status}`);
  }
  return json as T;
}

// ─── Auth ───────────────────────────────────────────────────────────

export interface AuthUser {
  id: string;
  email: string;
  rolle: 'admin';
}

export async function login(email: string, password: string) {
  const res = await call<{ ok: true; token: string; user: AuthUser }>(
    '/api/admin/auth/login',
    { method: 'POST', body: JSON.stringify({ email, password }) },
    false,
  );
  setToken(res.token);
  return res;
}

export async function logout() {
  try {
    await call('/api/admin/auth/logout', { method: 'POST' });
  } catch { /* ignore */ }
  setToken(null);
}

export async function getMe() {
  return call<{ ok: true; user: AuthUser }>('/api/admin/auth/me');
}

// ─── User-Verwaltung ────────────────────────────────────────────────

export interface UserPublic {
  id: string;
  email: string;
  rolle: 'admin';
  created_at: string;
}

export async function listUsers() {
  return call<{ ok: true; users: UserPublic[] }>('/api/admin/users');
}

export async function createNewUser(email: string, password: string) {
  return call<{ ok: true; user: UserPublic }>('/api/admin/users', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export async function deleteUserApi(id: string) {
  return call(`/api/admin/users/${id}`, { method: 'DELETE' });
}

export async function changeOwnPassword(oldPassword: string, newPassword: string) {
  return call('/api/admin/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
  });
}

export async function resetUserPassword(id: string, newPassword: string) {
  return call(`/api/admin/users/${id}/password`, {
    method: 'POST',
    body: JSON.stringify({ new_password: newPassword }),
  });
}

// ─── Rezepte (unverändert) ──────────────────────────────────────────

export interface RezeptSummary {
  id: string;
  titel: string;
  schweregrad?: 'leicht' | 'mittel' | 'schwer';
  tipps: number;
  warnsignale: number;
}

export interface Tipp {
  kategorie: 'hausmittel' | 'verhalten' | 'ernaehrung' | 'rezeptfrei' | 'ruhe';
  titel: string;
  beschreibung: string;
}

export interface RezeptFull {
  id: string;
  titel: string;
  kurzbeschreibung?: string;
  regionen: string[];
  schlagwoerter: string[];
  schweregrad?: 'leicht' | 'mittel' | 'schwer';
  inhalt_html?: string;
  tipps: Tipp[];
  warnsignale: string[];
  arzt_aufsuchen_wenn?: string | null;
  krankmeldung_sinnvoll?: boolean;
  letzte_aktualisierung?: string;
  quelle?: string;
}

export async function listRezepte() {
  return call<{ ok: true; rezepte: RezeptSummary[] }>('/api/admin/rezepte');
}

export async function getRezept(id: string) {
  return call<{ ok: true; rezept: RezeptFull }>(`/api/admin/rezepte/${id}`);
}

export async function saveRezept(id: string, data: RezeptFull) {
  return call(`/api/admin/rezepte/${id}`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteRezept(id: string) {
  return call(`/api/admin/rezepte/${id}`, { method: 'DELETE' });
}

// ─── Praxen-Verwaltung ──────────────────────────────────────────────

export interface PraxisPublic {
  id: string;
  qr_token: string;
  name: string;
  email: string;
  telefon?: string;
  adresse?: string;
  notiz?: string;
  created_at: string;
  updated_at: string;
}

export interface CreatePraxisInput {
  name: string;
  email: string;
  password: string;
  telefon?: string;
  adresse?: string;
  notiz?: string;
}

export interface UpdatePraxisInput {
  name?: string;
  email?: string;
  telefon?: string | null;
  adresse?: string | null;
  notiz?: string | null;
  password?: string;
  rotate_token?: boolean;
}

export async function listPraxen() {
  return call<{ ok: true; praxen: PraxisPublic[] }>('/api/admin/praxen');
}

export async function getPraxis(id: string) {
  return call<{ ok: true; praxis: PraxisPublic }>(`/api/admin/praxen/${id}`);
}

export async function createPraxisApi(input: CreatePraxisInput) {
  return call<{ ok: true; praxis: PraxisPublic }>('/api/admin/praxen', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function updatePraxisApi(id: string, input: UpdatePraxisInput) {
  return call<{ ok: true; praxis: PraxisPublic }>(`/api/admin/praxen/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export async function deletePraxisApi(id: string) {
  return call(`/api/admin/praxen/${id}`, { method: 'DELETE' });
}

/** Format, das im QR-Code steckt. Mobile-App scannt es und ruft danach /api/praxis/info/:token ab. */
export function praxisQrPayload(p: PraxisPublic): string {
  return JSON.stringify({
    praxis_id: p.id,
    qr_token: p.qr_token,
    praxis_name: p.name,
  });
}

// ─── Statistik ──────────────────────────────────────────────────────

export interface Stats {
  generated_at: string;
  total_events: number;
  rezept_views: {
    last_30_days: number;
    last_7_days: number;
    top_rezepte: { rezept_id: string; count: number }[];
    pro_tag: { date: string; count: number }[];
    pro_source: { source: string; count: number }[];
  };
  krankmeldungen: {
    last_30_days: number;
    last_7_days: number;
    durchschnitt_tage: number | null;
    pro_tag: { date: string; count: number }[];
    pro_rezept: { rezept_id: string; count: number }[];
    anteil_an_praxis: { praxis: number; arbeitgeber: number };
  };
  praxis_qr_scans: {
    last_30_days: number;
  };
}

export interface PatientenProPraxis {
  praxis_id: string;
  praxis_name: string;
  patienten_count: number;
}

export interface FragenProPraxis {
  praxis_id: string;
  praxis_name: string;
  threads_total: number;
  threads_offen: number;
  threads_beantwortet: number;
  threads_geschlossen: number;
  messages_total: number;
}

export async function getStats() {
  return call<{
    ok: true;
    stats: Stats;
    patienten_pro_praxis?: PatientenProPraxis[];
    fragen_pro_praxis?: FragenProPraxis[];
  }>('/api/admin/stats');
}

// ─── Admin: Fragen-Inhalte (pseudonymisiert) ──────────────────────

export interface AdminFrageView {
  id: string;
  praxis_id: string;
  praxis_name: string;
  rezept_id?: string;
  patient_pseudonym: string;
  titel: string;
  erste_frage: string;
  status: 'offen' | 'beantwortet' | 'geschlossen';
  anzahl_nachrichten: number;
  hat_antwort_von_praxis: boolean;
  created_at: string;
  updated_at: string;
}

export async function listAdminFragen() {
  return call<{ ok: true; threads: AdminFrageView[] }>('/api/admin/fragen');
}

